/**
 * Zoom/pan image with HammerJS using stable pinch-center math.
 * Returns a Hammer instance synchronously.
 *
 * @param {Function} Hammer - HammerJS constructor
 * @param {HTMLElement} el  - The <img> receiving zoom
 * @return {Hammer} An instance of Hammer
 */
export default function imageZoom(Hammer, el) {
  const img = el;
  const container = img.parentElement;

  // Configuration
  const MIN_SCALE = 1;
  const MAX_SCALE = 8;

  // Current transform state
  let scale = 1;
  let lastScale = 1;  // Scale committed at end of last gesture
  let tx = 0;         // Translation X in CSS pixels
  let ty = 0;         // Translation Y in CSS pixels
  let lastTx = 0;     // Translation X committed at end of last gesture
  let lastTy = 0;     // Translation Y committed at end of last gesture

  // Gesture state - captured at start of each pinch
  let gestureStartScale = 1;
  let gestureStartTx = 0;
  let gestureStartTy = 0;
  let pinchCx = 0;  // Pinch center in image-space coordinates
  let pinchCy = 0;

  // Gesture control flag
  let isPinching = false;

  // Prepare element styles to prevent browser interference
  if (container) container.style.touchAction = 'none';
  img.style.transformOrigin = '0 0';
  img.style.willChange = 'transform';
  img.style.userSelect = 'none';
  img.style.webkitUserDrag = 'none';
  img.style.transition = 'none';

  // Initialize HammerJS
  const hammertime = new Hammer(container || img, { domEvents: true });
  hammertime.get('pinch').set({ enable: true });

  /**
   * Clamp scale to MIN_SCALE and MAX_SCALE boundaries
   */
  function clampScale(s) {
    if (s < MIN_SCALE) return MIN_SCALE;
    if (s > MAX_SCALE) return MAX_SCALE;
    return s;
  }

  /**
   * Clamp translation to keep image within viewport bounds.
   * If image is smaller than viewport on any axis, center it.
   *
   * @param {number} nx - Proposed translation X
   * @param {number} ny - Proposed translation Y
   * @param {number} currentScale - Current scale to use for boundary calculation
   * @return {Object} Clamped coordinates {x, y}
   */
  function clampTranslate(nx, ny, currentScale) {
    const baseW = img.offsetWidth || img.clientWidth || 0;
    const baseH = img.offsetHeight || img.clientHeight || 0;

    const viewportW = (container ? container.clientWidth : baseW) || baseW;
    const viewportH = (container ? container.clientHeight : baseH) || baseH;

    const scaledW = baseW * currentScale;
    const scaledH = baseH * currentScale;

    let minX;
    let maxX;
    let minY;
    let maxY;

    // If scaled image is smaller than viewport, center it
    if (scaledW <= viewportW) {
      const centeredX = (viewportW - scaledW) / 2;
      minX = centeredX;
      maxX = centeredX;
    } else {
      // Otherwise, allow panning but keep edges within viewport
      minX = viewportW - scaledW;  // Left edge limit
      maxX = 0;                     // Right edge limit
    }

    if (scaledH <= viewportH) {
      const centeredY = (viewportH - scaledH) / 2;
      minY = centeredY;
      maxY = centeredY;
    } else {
      minY = viewportH - scaledH;  // Top edge limit
      maxY = 0;                     // Bottom edge limit
    }

    return {
      x: Math.max(minX, Math.min(nx, maxX)),
      y: Math.max(minY, Math.min(ny, maxY)),
    };
  }

  /**
   * Apply current transform state to the image element.
   * Resets to identity transform if scale is at minimum.
   */
  function apply() {
    // Reset everything if we're at minimum scale
    if (scale <= 1.0001) {
      scale = 1;
      tx = 0;
      ty = 0;
      lastScale = 1;
      lastTx = 0;
      lastTy = 0;
    }
    img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale3d(${scale}, ${scale}, 1)`;
  }

  // --- Pan gesture handlers ---

  hammertime.on('panstart', () => {
    // Don't start pan if we're in the middle of a pinch
    if (isPinching) return;
    lastTx = tx;
    lastTy = ty;
  });

  hammertime.on('pan', (e) => {
    // Only allow panning when zoomed in and not pinching
    if (scale <= 1 || isPinching) return;
    const clamped = clampTranslate(lastTx + e.deltaX, lastTy + e.deltaY, scale);
    tx = clamped.x;
    ty = clamped.y;
    apply();
  });

  // --- Pinch gesture handlers ---

  hammertime.on('pinchstart', (e) => {
    // Ignore phantom pinchstart events that can fire immediately after pinchend
    if (isPinching) {
      return;
    }

    isPinching = true;

    // Capture the committed state from the previous gesture
    gestureStartScale = lastScale;
    gestureStartTx = lastTx;
    gestureStartTy = lastTy;

    // Calculate pinch center in viewport coordinates
    const ref = (container || img).getBoundingClientRect();
    const px = e.center.x - ref.left;
    const py = e.center.y - ref.top;

    // Convert pinch center to image-space coordinates
    // This allows us to keep the point under the fingers stationary during zoom
    pinchCx = (px - gestureStartTx) / gestureStartScale;
    pinchCy = (py - gestureStartTy) / gestureStartScale;
  });

  hammertime.on('pinch', (e) => {
    // Calculate new scale relative to gesture start
    scale = clampScale(gestureStartScale * e.scale);

    // Keep the pinch center stationary using the formula:
    // t' = t0 + c*(s0 - s')
    // Where t0 is initial translation, c is pinch center, s0 is initial scale, s' is new scale
    const nx = gestureStartTx + (pinchCx * (gestureStartScale - scale));
    const ny = gestureStartTy + (pinchCy * (gestureStartScale - scale));

    tx = nx;
    ty = ny;

    apply();
  });

  hammertime.on('pinchend', (e) => {
    if (!isPinching) {
      return;
    }

    e.preventDefault();

    // Calculate viewport and scaled image dimensions
    const baseW = img.offsetWidth || img.clientWidth || 0;
    const baseH = img.offsetHeight || img.clientHeight || 0;
    const viewportW = (container ? container.clientWidth : baseW) || baseW;
    const viewportH = (container ? container.clientHeight : baseH) || baseH;
    const scaledW = baseW * scale;
    const scaledH = baseH * scale;

    // Determine if clamping is needed
    const needsClampX = (scaledW > viewportW && (tx > 0 || tx < viewportW - scaledW));
    const needsClampY = (scaledH > viewportH && (ty > 0 || ty < viewportH - scaledH));
    const needsCentering = (scaledW <= viewportW || scaledH <= viewportH);

    // Only clamp if image is out of bounds or needs centering
    if (needsClampX || needsClampY || needsCentering) {
      const clamped = clampTranslate(tx, ty, scale);
      tx = clamped.x;
      ty = clamped.y;
      apply();
    }

    // Commit the final values for next gesture
    lastScale = scale;
    lastTx = tx;
    lastTy = ty;

    // Delay clearing isPinching flag to prevent phantom pinchstart events
    // that can fire immediately after pinchend from re-anchoring the image
    setTimeout(() => {
      isPinching = false;
    }, 50);
  });

  // --- Double-tap to zoom ---

  hammertime.add(new Hammer.Tap({ event: 'doubletap', taps: 2 }));
  hammertime.on('doubletap', (e) => {
    // Calculate tap position
    const ref = (container || img).getBoundingClientRect();
    const px = e.center.x - ref.left;
    const py = e.center.y - ref.top;

    // Use current committed state as gesture start
    gestureStartScale = lastScale;
    gestureStartTx = lastTx;
    gestureStartTy = lastTy;

    // Convert tap point to image-space coordinates
    pinchCx = (px - gestureStartTx) / gestureStartScale;
    pinchCy = (py - gestureStartTy) / gestureStartScale;

    // Zoom to 2x
    scale = clampScale(gestureStartScale * 2);

    // Calculate new position to keep tap point stationary
    const nx = gestureStartTx + (pinchCx * (gestureStartScale - scale));
    const ny = gestureStartTy + (pinchCy * (gestureStartScale - scale));

    // Clamp and apply
    const clamped = clampTranslate(nx, ny, scale);
    tx = clamped.x;
    ty = clamped.y;

    // Commit values
    lastScale = scale;
    lastTx = tx;
    lastTy = ty;

    apply();
  });

  return hammertime;
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/zoom/image-zoom.js